import { ReactNode } from "react";
import { useSelector } from "react-redux";
import { Navigate } from "react-router-dom";

import { RootState } from "@/store";

interface PermissionGuardProps {
  children: ReactNode;
  requireAll?: boolean;
  requiredPermissions?: string[];
}

/**
 * PermissionGuard: Protects routes based on user permissions
 * @param children - Component to render if user has permission
 * @param requiredPermissions - Array of required permissions
 * @param requireAll - If true, user must have ALL permissions; if false, only ANY permission is required
 * 
 * Note: Admin users automatically have access to all protected routes
 */
export default function PermissionGuard({
  children,
  requireAll = false,
  requiredPermissions,
}: PermissionGuardProps) {
  const user = useSelector((state: RootState) => state.auth.user);

  // Admin users automatically have access to everything
  if (user?.role === "admin") {
    return <>{children}</>;
  }

  const userPermissions = user?.permissions || [];

  // If no specific permissions required, allow access
  if (!requiredPermissions || requiredPermissions.length === 0) {
    return <>{children}</>;
  }

  // Check permissions based on requireAll flag
  const hasPermission = requireAll
    ? requiredPermissions.every((perm) => userPermissions.includes(perm))
    : requiredPermissions.some((perm) => userPermissions.includes(perm));

  if (!hasPermission) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="w-full max-w-md p-8 text-center">
          <div className="mb-4 text-6xl">🔒</div>
          <h1 className="mb-2 text-2xl font-bold text-error">
            Insufficient Permissions
          </h1>
          <p className="mb-6 text-base-content/70">
            You do not have the required permissions to access this resource.
            Please contact your administrator if you believe this is a mistake.
          </p>
          <a className="btn btn-primary" href="/dashboard">
            Back to Dashboard
          </a>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
